import React from 'react';
import { Form, Upload, Button, Row, Col, Image, Divider, Card, Space, Alert } from 'antd';
import { 
  UploadOutlined, 
  QrcodeOutlined, 
  DeleteOutlined,
  EyeOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CloseOutlined
} from '@ant-design/icons';
import { MenuItem } from '@/types';

interface ImagesAndQRTabProps {
  fileList: any[];
  additionalImages: any[];
  existingDetailedImages: string[];
  menuItem: MenuItem | null;
  loading: boolean;
  onMainImageChange: (info: any) => void;
  onAdditionalImagesChange: (info: any) => void;
  onRemoveExistingDetailedImage: (imageIndex: number) => void;
  beforeUpload: (file: any) => boolean;
  onGenerateQR?: (itemId: number) => void;
  onRemoveQR?: (itemId: number) => void;
}

export const ImagesAndQRTab: React.FC<ImagesAndQRTabProps> = ({
  fileList,
  additionalImages,
  existingDetailedImages,
  menuItem,
  loading,
  onMainImageChange,
  onAdditionalImagesChange,
  onRemoveExistingDetailedImage,
  beforeUpload,
  onGenerateQR,
  onRemoveQR
}) => {
  const downloadQRCode = () => {
    if (menuItem?.qr_code_url) {
      const link = document.createElement('a');
      link.href = menuItem.qr_code_url;
      link.download = `qr_${menuItem.slug}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const openQRCode = () => {
    if (menuItem?.qr_code_url) {
      window.open(menuItem.qr_code_url, '_blank');
    }
  };

  return (
    <div>
      {/* Основное изображение */}
      <Form.Item
        name="image"
        label="Основное изображение блюда"
      >
        <div>
          <Upload
            fileList={fileList}
            onChange={onMainImageChange}
            beforeUpload={beforeUpload}
            accept=".jpg,.jpeg,.png,.webp"
            maxCount={1}
            listType="picture-card"
          >
            {fileList.length === 0 && (
              <div>
                <UploadOutlined />
                <div style={{ marginTop: 8 }}>Загрузить</div>
              </div>
            )}
          </Upload>
          <div style={{ color: '#666', fontSize: '12px', marginTop: '8px' }}>
            Рекомендуемый размер: 800x600 пикселей, не более 5MB
          </div>
          
          {/* Показываем текущее изображение если есть */}
          {menuItem?.image_url && (
            <div style={{ marginTop: 16 }}>
              <p>Текущее изображение:</p>
              <Image
                width={150}
                src={menuItem.image_url}
                alt={menuItem.name}
                style={{ border: '1px solid #d9d9d9', borderRadius: '6px' }}
                fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RUG8O+L"
              />
            </div>
          )}
        </div>
      </Form.Item>

      <Divider />

      {/* Дополнительные изображения */}
      <Form.Item
        label="Дополнительные изображения"
      >
        <div>
          {/* НОВОЕ: Существующие дополнительные изображения */}
          {existingDetailedImages.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <p>Текущие дополнительные изображения ({existingDetailedImages.length}):</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                {existingDetailedImages.map((url, index) => (
                  <div key={index} style={{ position: 'relative' }}>
                    <Image
                      width={100}
                      height={100}
                      src={url}
                      alt={`Дополнительное изображение ${index + 1}`}
                      style={{ 
                        border: '1px solid #d9d9d9', 
                        borderRadius: '6px',
                        objectFit: 'cover'
                      }}
                      fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RUG8O+L"
                    />
                    <Button
                      type="text"
                      icon={<CloseOutlined />}
                      size="small"
                      danger
                      style={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        background: 'rgba(255,0,0,0.8)',
                        color: 'white',
                        border: 'none',
                        width: '20px',
                        height: '20px',
                        padding: 0
                      }}
                      onClick={() => onRemoveExistingDetailedImage(index)}
                    />
                    <div style={{
                      position: 'absolute',
                      bottom: 4,
                      left: 4,
                      background: 'rgba(0,0,0,0.7)',
                      color: 'white',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      fontSize: '12px'
                    }}>
                      {index + 1}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <Upload
            fileList={additionalImages}
            onChange={onAdditionalImagesChange}
            beforeUpload={beforeUpload}
            accept=".jpg,.jpeg,.png,.webp"
            multiple
            listType="picture-card"
          >
            <div>
              <UploadOutlined />
              <div style={{ marginTop: 8 }}>Добавить</div>
            </div>
          </Upload>
          <div style={{ color: '#666', fontSize: '12px', marginTop: '8px' }}>
            Детальные фотографии блюда для страницы товара
          </div>
          
          {/* Информация если нет дополнительных изображений */}
          {(!existingDetailedImages || existingDetailedImages.length === 0) && (
            <div style={{ 
              marginTop: 16, 
              padding: '12px', 
              backgroundColor: '#f5f5f5', 
              borderRadius: '6px',
              textAlign: 'center'
            }}>
              <p style={{ margin: 0, color: '#666' }}>
                Дополнительные изображения не загружены
              </p>
            </div>
          )}
        </div>
      </Form.Item>

      <Divider />

      {/* QR-код */}
      <Card title="Управление QR-кодом">
        {menuItem?.qr_code_url ? (
          <div>
            <Row gutter={16} align="middle">
              <Col span={12}>
                <div style={{ textAlign: 'center' }}>
                  <Image
                    width={200}
                    src={menuItem.qr_code_url}
                    alt="QR код"
                    style={{ border: '1px solid #d9d9d9', borderRadius: '6px' }}
                    fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RUG8O+L"
                  />
                  <div style={{ marginTop: 8, color: '#666' }}>
                    Сгенерирован: {menuItem.qr_generated_at ? 
                      new Date(menuItem.qr_generated_at).toLocaleString('ru-RU') : 
                      'Неизвестно'
                    }
                  </div>
                </div>
              </Col>
              <Col span={12}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Alert
                    message="QR-код активен"
                    description={`Ведет на страницу: /product/${menuItem.slug}/detail/`}
                    type="success"
                    icon={<CheckCircleOutlined />}
                    showIcon
                  />
                  
                  <Button 
                    type="primary" 
                    icon={<EyeOutlined />}
                    onClick={openQRCode}
                    block
                  >
                    Просмотреть в полном размере
                  </Button>
                  
                  <Button 
                    icon={<DownloadOutlined />}
                    onClick={downloadQRCode}
                    block
                  >
                    Скачать QR-код
                  </Button>
                  
                  <Button 
                    icon={<QrcodeOutlined />}
                    onClick={() => onGenerateQR && onGenerateQR(menuItem.id)}
                    loading={loading}
                    block
                  >
                    Перегенерировать
                  </Button>
                  
                  <Button 
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => onRemoveQR && onRemoveQR(menuItem.id)}
                    loading={loading}
                    block
                  >
                    Удалить QR-код
                  </Button>
                </Space>
              </Col>
            </Row>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <QrcodeOutlined style={{ fontSize: 64, color: '#ccc', marginBottom: 16 }} />
            <div style={{ marginBottom: 16 }}>
              {menuItem ? (
                <div>
                  <p>QR-код для этого блюда не создан</p>
                  <Button 
                    type="primary"
                    icon={<QrcodeOutlined />}
                    onClick={() => onGenerateQR && onGenerateQR(menuItem.id)}
                    loading={loading}
                    size="large"
                  >
                    Сгенерировать QR-код
                  </Button>
                </div>
              ) : (
                <Alert
                  message="Сначала сохраните блюдо"
                  description="QR-код можно создать только после сохранения блюда"
                  type="info"
                  icon={<ExclamationCircleOutlined />}
                  showIcon
                />
              )}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};